/* eslint-disable ember/classic-decorator-hooks */
import EmberObject, { set } from '@ember/object';

import FilterConstants from '../../filter-constants';
import * as CircuitUtils from '../utils/circuit';
import * as CapacitorToleranceUtils from '../tolerances/capacitor-tolerance-utils';
import * as CommonUtils from '../../utils/common';
import { computeNominalValue } from '../tolerances/tolerance-utils';

//
// The idea behind a these components (capacitor, resistor, inductor) is we have classes that encapsulate
// how each of them are created.
// c1's difference from capacitor  class is c1 gets the nominal and adjust it
// to within the limits. Capacitor only computes the closest nominalValue.
// sizingAdjustedValue for c1 is adjusted based from the computed r1 value.
// sizingAdjustedValue for capacitor class is adjusted based from the ratio
// of c1 value to its limits.
//
// Lifecycle: This component is created with the
// 1. ideal value (component value based from computation, aka. textbook component in the old app, or ideal value),
// 2. a multiplier to get the minimum limit.
// 3. a multiplier to get the maximum limit.
//
// The ideal value is not directly used in here. It's only used to get the limits which are in turn used to compute
// a value based from c1's current value in comparison to its minimum and maximum (rcRatio).
//
// Construction: C1.create({idealValue: idealValue, capacitorMaximumLimitMultiplier: capacitorMaximumLimitMultiplier, capacitorMinimumLimitMultiplier: capacitorMinimumLimitMultiplier})
// Hash:
// hash = {
//              idealValue: { number },
//              capacitorMaximumLimitMultiplier: { number },
//              capacitorMinimumLimitMultiplier: { number }
//        }
// Create: C1.create(hash);
// @inputProperty { number } idealValue - The computed value based from a given c1.
// @inputProperty { number } capacitorMaximumLimitMultiplier - A value computed from the ideal values of all the components. Used to compute this component's maximum limit.
// @inputProperty { number } capacitorMinimumLimitMultiplier - A value computed from the ideal values of all the components used to compute this component's minimum limit.
// @inputProperty { FilterConstants.sizing } sizing - Indication whether the value of r1 is going towards its max or min. the value of this component is adjusted depending on the sizing
//
// @getterProperty { number } nominalValue - The computed commercial value. This matches the value of capacitors that are available in the market.
// @getterProperty { number[] } toleranceArray - The nominal value multiplied to each of the elements of the array. The array consists of different tolerance multipliers.
// @getterProperty { FilterConstants.capacitorTolerance } finalTolerance - If the desired tolerance fails to get the nominal, there's a process that goes through all the available tolerance options to get the nominal value.
// @getterProperty { FilterConstants.capacitorTolerance } desiredTolerance - the selected capacitor tolerance or the default if none.
// @getterProperty { bool } isCommercial - True if this component is already converted into a commercial capacitor. False if not.
//
// @computed { number } maximum - the capacitor instance's maximum limit
// @computed { number } minimum - the capacitor instance's minimum limit
// @computed { number } sizingAdjustedValue - The computed ideal value adjusted to be within the limits.
// @computed { number } value - the ideal value if the component is not commercial, the nominal value if the component is already converted to a commercial one.
// @method { this } toCommercialComponent() - converts the component into its commercial version.
// @method { number } getNominalValue() - gets the nominal value. Can only be accessed when the component is already a commercial one.
export default class C1 extends EmberObject {
  name = FilterConstants.capacitorNames.c1;
  idealValue = null;
  maximumLimitMultiplier = null;
  capacitorMinimumLimitMultiplier = null;
  sizing = null;
  isCommercial = null;
  nominalValue = null;
  toleranceArray = null;
  finalTolerance = null;
  desiredTolerance = null;
  toleranceInput = null;

  init() {
    super.init();
    set(this, 'isCommercial', false);

    this.minimum =
      CommonUtils.isNullOrUndefined(this.minimum) || this.minimum === 0
        ? CircuitUtils.computeCapacitorLimit(this.idealValue, this.capacitorMinimumLimitMultiplier)
        : this.minimum;
    this.maximum =
      CommonUtils.isNullOrUndefined(this.maximum) || this.maximum === 0
        ? CircuitUtils.computeCapacitorLimit(this.idealValue, this.capacitorMaximumLimitMultiplier)
        : this.maximum;
    this.value = this.adjustCapacitorValueToCircuitSizing(this.idealValue, this.sizing, this.minimum, this.maximum);
  }

  // ToCommercialComponent converts the component to the commercial equivalent.
  toCommercialComponent(toleranceInput) {
    set(this, 'toleranceInput', toleranceInput);
    let valueAdjusted = this.adjustCapacitorValueToCircuitSizing(
      this.idealValue,
      this.sizing,
      this.minimum,
      this.maximum,
    );
    let tolInput = !toleranceInput ? CircuitUtils.createToleranceInputDefaults() : toleranceInput;
    // var nominalValue = CapacitorToleranceUtils.computeNominalValue(valueAdjusted, tolInput, 'closest');
    var nominalValue = computeNominalValue(valueAdjusted, tolInput.capacitorPreferredSeries, 'closest');
    var minimum = this.minimum;
    var maximum = this.maximum;

    if (nominalValue < minimum) {
      // nominalValue = CapacitorToleranceUtils.computeNominalValue(minimum, tolInput, 'up');
      nominalValue = computeNominalValue(minimum, tolInput.capacitorPreferredSeries, 'up');
    } else if (nominalValue > maximum) {
      // nominalValue = CapacitorToleranceUtils.computeNominalValue(maximum, tolInput, 'down');
      nominalValue = computeNominalValue(maximum, tolInput.capacitorPreferredSeries, 'down');
    }

    let toleranceValues = CapacitorToleranceUtils.getToleranceValues(nominalValue, tolInput.capacitorTolerance);
    set(this, 'value', nominalValue);
    set(this, 'toleranceArray', toleranceValues.toleranceArray);
    set(this, 'finalTolerance', toleranceValues.finalTolerance);
    set(this, 'desiredTolerance', tolInput.capacitorTolerance);
    set(this, 'isCommercial', true);

    return this;
  }

  getNominalValue(toleranceInput) {
    if (this.isCommercial) return this.value;

    let tolInput = !toleranceInput ? CircuitUtils.createToleranceInputDefaults() : toleranceInput;
    // var nominalValue = CapacitorToleranceUtils.computeNominalValue(this.value, tolInput, 'closest');
    var nominalValue = computeNominalValue(this.value, tolInput.capacitorPreferredSeries, 'closest');
    return nominalValue;
  }

  adjustCapacitorValueToCircuitSizing(idealValue, circuitSizing, capacitorMinimum, capacitorMaximum) {
    let result = idealValue;
    // if the resistor value is towards the tiny end, capacitor should be big and vice versa
    if (circuitSizing === FilterConstants.sizing.tiny) {
      result = capacitorMaximum;
    } else if (circuitSizing === FilterConstants.sizing.big) {
      result = capacitorMinimum;
    }

    return result;
  }
}
